import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Country, State, City } from "country-state-city";
import "./AddAccount.css";

const AddAccount = ({
  formData: propFormData,
  handleChange: propHandleChange,
  countries: propCountries,
  selectedCountryIso: propSelectedCountryIso,
  selectedStateIso: propSelectedStateIso,
  stateList: propStateList,
  handleCountryChange: propHandleCountryChange,
  handleStateChange: propHandleStateChange,
  onSave,
}) => {
  const isStandalone = !propFormData;

  const [localFormData, setLocalFormData] = useState({
    account_type: "",
    account_name: "",
    address: "",
    address2: "",
    country: "",
    state: "",
    city: "",
    zip: "",
    telephone: "",
    email: "",
    website: "",
    time_zone: "",
    status: "",
    ddxEnable: false,
    addedBy: "",
  });

  const formData = isStandalone ? localFormData : propFormData;

  const countries = propCountries || Country.getAllCountries();

  const [selectedCountryIso, setSelectedCountryIso] = useState(
    propSelectedCountryIso || ""
  );

  const [selectedStateIso, setSelectedStateIso] = useState(
    propSelectedStateIso || ""
  );

  const [stateList, setStateList] = useState(propStateList || []);
  const [cityList, setCityList] = useState([]);

  const navigate = useNavigate();

  const prevCountryIso = useRef(selectedCountryIso);
  const prevStateIso = useRef(selectedStateIso);

  const [customStates, setCustomStates] = useState({});
  const [showAddState, setShowAddState] = useState(false);
  const [newStateName, setNewStateName] = useState("");

  const [labId, setLabId] = useState("");

  /* ================= CUSTOM STATES ================= */

  useEffect(() => {
    const storedCustoms = localStorage.getItem("customStates");

    if (storedCustoms) {
      setCustomStates(JSON.parse(storedCustoms));
    }
  }, []);

  /* ================= LAB ID ================= */

  useEffect(() => {
    const storedLabId = localStorage.getItem("userLabId");

    if (storedLabId) {
      setLabId(storedLabId);

      if (isStandalone) {
        setLocalFormData((prev) => ({
          ...prev,
          addedBy: storedLabId,
        }));
      }
    }
  }, [isStandalone]);

  /* ================= PROPS SYNC ================= */

  useEffect(() => {
    setSelectedCountryIso(propSelectedCountryIso || "");
  }, [propSelectedCountryIso]);

  useEffect(() => {
    setSelectedStateIso(propSelectedStateIso || "");
  }, [propSelectedStateIso]);

  useEffect(() => {
    setStateList(propStateList || []);
  }, [propStateList]);

  /* ================= COUNTRY / STATE ================= */

  useEffect(() => {
    if (selectedCountryIso) {
      const baseStates =
        propStateList ||
        State.getStatesOfCountry(selectedCountryIso);

      const customs = customStates[selectedCountryIso] || [];

      const combined = [...baseStates, ...customs].sort((a, b) =>
        a.name.localeCompare(b.name)
      );

      setStateList(combined);

      if (selectedCountryIso !== prevCountryIso.current) {
        setSelectedStateIso("");
        setCityList([]);

        if (isStandalone) {
          setLocalFormData((prev) => ({
            ...prev,
            state: "",
            city: "",
          }));
        } else if (propHandleChange) {
          propHandleChange({
            target: {
              name: "state",
              value: "",
            },
          });

          propHandleChange({
            target: {
              name: "city",
              value: "",
            },
          });
        }

        if (propHandleStateChange && selectedStateIso !== "") {
          propHandleStateChange({
            target: {
              value: "",
            },
          });
        }
      }
    } else {
      setStateList([]);
      setCityList([]);
    }

    prevCountryIso.current = selectedCountryIso;
  }, [
    selectedCountryIso,
    customStates,
    isStandalone,
    propHandleChange,
    propHandleStateChange,
    propStateList,
  ]);

  /* ================= CITIES ================= */

  useEffect(() => {
    if (selectedCountryIso && selectedStateIso) {
      const cities = City.getCitiesOfState(
        selectedCountryIso,
        selectedStateIso
      );

      setCityList(cities || []);

      if (selectedStateIso !== prevStateIso.current) {
        if (isStandalone) {
          setLocalFormData((prev) => ({
            ...prev,
            city: "",
          }));
        } else if (propHandleChange) {
          propHandleChange({
            target: {
              name: "city",
              value: "",
            },
          });
        }
      }
    } else {
      setCityList([]);
    }

    prevStateIso.current = selectedStateIso;
  }, [
    selectedCountryIso,
    selectedStateIso,
    isStandalone,
    propHandleChange,
  ]);

  /* ================= ADD NEW STATE ================= */

  const handleAddState = () => {
    const trimmedName = newStateName.trim();

    if (!trimmedName) {
      alert("Please enter a state name.");
      return;
    }

    const exists = stateList.some(
      (s) =>
        s.name.toLowerCase() === trimmedName.toLowerCase()
    );

    if (exists) {
      alert("This state already exists.");
      return;
    }

    const newIso = `CU_${trimmedName
      .replace(/\s+/g, "_")
      .toUpperCase()}`;

    const newState = {
      isoCode: newIso,
      name: trimmedName,
      countryCode: selectedCountryIso,
    };

    const updatedCustoms = {
      ...customStates,
      [selectedCountryIso]: [
        ...(customStates[selectedCountryIso] || []),
        newState,
      ],
    };

    setCustomStates(updatedCustoms);

    localStorage.setItem(
      "customStates",
      JSON.stringify(updatedCustoms)
    );

    const updatedList = [...stateList, newState].sort(
      (a, b) => a.name.localeCompare(b.name)
    );

    setStateList(updatedList);

    handleStateChange({
      target: {
        value: newIso,
      },
    });

    setShowAddState(false);
    setNewStateName("");
  };

  /* ================= NORMAL INPUT CHANGE ================= */

  const handleLocalChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (isStandalone) {
      setLocalFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    } else if (propHandleChange) {
      propHandleChange(e);
    }
  };

  /* ================= COUNTRY CHANGE ================= */

  const handleCountryChange = (e) => {
    const iso = e.target.value;

    const country = countries.find(
      (c) => c.isoCode === iso
    );

    const countryName = country ? country.name : "";

    if (propHandleCountryChange) {
      propHandleCountryChange(e);
    } else {
      setSelectedCountryIso(iso);
    }

    if (isStandalone) {
      setLocalFormData((prev) => ({
        ...prev,
        country: countryName,
      }));
    } else if (propHandleChange) {
      propHandleChange({
        target: {
          name: "country",
          value: countryName,
        },
      });
    }
  };

  /* ================= STATE CHANGE ================= */

  const handleStateChange = (e) => {
    const iso = e.target.value;

    const state = stateList.find(
      (s) => s.isoCode === iso
    );

    const stateName = state ? state.name : "";

    if (propHandleStateChange) {
      propHandleStateChange(e);
    } else {
      setSelectedStateIso(iso);
    }

    if (isStandalone) {
      setLocalFormData((prev) => ({
        ...prev,
        state: stateName,
      }));
    } else if (propHandleChange) {
      propHandleChange({
        target: {
          name: "state",
          value: stateName,
        },
      });
    }
  };

  /* ================= CITY CHANGE ================= */

  const handleCityChange = (e) => {
    const cityName = e.target.value;

    if (isStandalone) {
      setLocalFormData((prev) => ({
        ...prev,
        city: cityName,
      }));
    } else if (propHandleChange) {
      propHandleChange({
        target: {
          name: "city",
          value: cityName,
        },
      });
    }
  };

  /* ================= SUBMIT ================= */

  const handleSubmit = async (e) => {
    e.preventDefault();

    const formDataToSubmit = {
      ...formData,
      addedBy: labId || formData.addedBy,
    };

    if (!formDataToSubmit.addedBy) {
      alert(
        "Cannot create account: Lab ID not found. Please login again."
      );
      return;
    }

    try {
      if (onSave) {
        onSave(formDataToSubmit);
      } else {
        await axios.post(
          "https://hms.backend.hiragroupcloud.com/api/account/add-account",
          formDataToSubmit
        );

        navigate("/accountTable");
      }
    } catch (error) {
      console.error(
        "There was an error submitting the form:",
        error
      );

      alert(
        "Failed to create account. Please try again."
      );
    }
  };

  return (
    <div className="add-account-form">

      <h3 className="add-account-title">
        Add New Account
      </h3>

      <form
        onSubmit={handleSubmit}
        className="account-form"
      >
        <div className="account-form-columns">

          {/* ================= LEFT COLUMN ================= */}

          <div className="form-column">

            <div className="form-row">
              <label className="form-label">
                Account Type{" "}
                <span className="required-star">*</span>
              </label>

              <div className="form-field">
                <select
                  name="account_type"
                  value={formData.account_type}
                  onChange={handleLocalChange}
                  className="form-select"
                  required
                >
                  <option value="">Select...</option>
                  <option value="Practice">Practice</option>
                  <option value="Lab">Lab</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <label className="form-label">
                Account Name{" "}
                <span className="required-star">*</span>
              </label>

              <div className="form-field">
                <input
                  type="text"
                  name="account_name"
                  value={formData.account_name}
                  onChange={handleLocalChange}
                  className="form-input"
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <label className="form-label">
                Address{" "}
                <span className="required-star">*</span>
              </label>

              <div className="form-field">
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleLocalChange}
                  className="form-input"
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <label className="form-label">
                Address 2
              </label>

              <div className="form-field">
                <input
                  type="text"
                  name="address2"
                  value={formData.address2}
                  onChange={handleLocalChange}
                  className="form-input"
                />
              </div>
            </div>

            <div className="form-row">
              <label className="form-label">
                Country{" "}
                <span className="required-star">*</span>
              </label>

              <div className="form-field">
                <select
                  value={selectedCountryIso}
                  onChange={handleCountryChange}
                  className="form-select"
                  required
                >
                  <option value="">
                    Select a country
                  </option>

                  {countries.map((country) => (
                    <option
                      key={country.isoCode}
                      value={country.isoCode}
                    >
                      {country.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-row state-row">
              <label className="form-label">
                State / Province{" "}
                <span className="required-star">*</span>
              </label>

              <div className="form-field">

                <select
                  value={selectedStateIso}
                  onChange={handleStateChange}
                  disabled={!selectedCountryIso}
                  className="form-select"
                  required
                >
                  <option value="">
                    Select a state
                  </option>

                  {stateList.map((state) => (
                    <option
                      key={state.isoCode}
                      value={state.isoCode}
                    >
                      {state.name}
                    </option>
                  ))}
                </select>

                {selectedCountryIso &&
                  !showAddState && (
                    <button
                      type="button"
                      onClick={() =>
                        setShowAddState(true)
                      }
                      className="add-state-btn"
                    >
                      Add New State
                    </button>
                  )}

                {showAddState && (
                  <div className="new-state-box">

                    <input
                      type="text"
                      value={newStateName}
                      onChange={(e) =>
                        setNewStateName(e.target.value)
                      }
                      placeholder="Enter state"
                      className="form-input"
                    />

                    <div className="state-action-buttons">

                      <button
                        type="button"
                        onClick={handleAddState}
                        className="save-state-btn"
                      >
                        Save
                      </button>

                      <button
                        type="button"
                        onClick={() => {
                          setShowAddState(false);
                          setNewStateName("");
                        }}
                        className="cancel-state-btn"
                      >
                        Cancel
                      </button>

                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* CREATE ACCOUNT - LEFT SIDE ON SMALL SCREEN */}

            <div className="create-account-row">

              <button
                type="submit"
                className="create-account-btn"
              >
                <span className="plus-icon">+</span>
                Create Account
              </button>

            </div>

          </div>

          {/* ================= RIGHT COLUMN ================= */}

          <div className="form-column">

            <div className="form-row">
              <label className="form-label">
                City{" "}
                <span className="required-star">*</span>
              </label>

              <div className="form-field">

                {selectedStateIso &&
                cityList.length > 0 ? (
                  <select
                    name="city"
                    value={formData.city}
                    onChange={handleCityChange}
                    className="form-select"
                    required
                  >
                    <option value="">
                      Select a city
                    </option>

                    {cityList.map((city) => (
                      <option
                        key={city.name}
                        value={city.name}
                      >
                        {city.name}
                      </option>
                    ))}
                  </select>
                ) : selectedStateIso ? (
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleLocalChange}
                    className="form-input"
                    placeholder="Enter city"
                    required
                  />
                ) : (
                  <select
                    disabled
                    className="form-select"
                  >
                    <option value="">
                      Select a city
                    </option>
                  </select>
                )}

              </div>
            </div>

            <div className="form-row">
              <label className="form-label">
                Zip / Postal Code{" "}
                <span className="required-star">*</span>
              </label>

              <div className="form-field">
                <input
                  type="text"
                  name="zip"
                  value={formData.zip}
                  onChange={handleLocalChange}
                  className="form-input"
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <label className="form-label">
                Telephone{" "}
                <span className="required-star">*</span>
              </label>

              <div className="form-field">
                <input
                  type="text"
                  name="telephone"
                  value={formData.telephone}
                  onChange={handleLocalChange}
                  className="form-input"
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <label className="form-label">
                Email
              </label>

              <div className="form-field">
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleLocalChange}
                  className="form-input"
                />
              </div>
            </div>

            <div className="form-row">
              <label className="form-label">
                Website
              </label>

              <div className="form-field">
                <input
                  type="text"
                  name="website"
                  value={formData.website}
                  onChange={handleLocalChange}
                  className="form-input"
                  placeholder="https://example.com/"
                />
              </div>
            </div>

            <div className="form-row">
              <label className="form-label">
                Time Zone{" "}
                <span className="required-star">*</span>
              </label>

              <div className="form-field">
                <select
                  name="time_zone"
                  value={formData.time_zone}
                  onChange={handleLocalChange}
                  className="form-select"
                  required
                >
                  <option value="">
                    Select a time zone
                  </option>

                  <option value="Asia/Karachi">
                    Asia/Karachi
                  </option>

                  <option value="Asia/Kolkata">
                    Asia/Kolkata
                  </option>

                  <option value="Asia/Dhaka">
                    Asia/Dhaka
                  </option>

                  <option value="Asia/Dubai">
                    Asia/Dubai
                  </option>

                  <option value="Europe/London">
                    Europe/London
                  </option>

                  <option value="America/New_York">
                    America/New York
                  </option>
                </select>
              </div>
            </div>

            {/* <div className="form-row ddx-row">
              <label className="form-label">
                DDX Enable{" "}
                <span className="required-star">*</span>
              </label>

              <div className="form-field ddx-field">

                <input
                  type="checkbox"
                  name="ddxEnable"
                  checked={formData.ddxEnable}
                  onChange={handleLocalChange}
                  className="ddx-checkbox"
                />

                <span className="help-icon">
                  ?
                </span>

              </div>
            </div> */}

            <div className="form-row">
              <label className="form-label">
                Status{" "}
                <span className="required-star">*</span>
              </label>

              <div className="form-field">
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleLocalChange}
                  className="form-select"
                  required
                >
                  <option value="">
                    Select...
                  </option>

                  <option value="active">
                    Active
                  </option>

                  <option value="inactive">
                    Inactive
                  </option>
                </select>
              </div>
            </div>

          </div>
        </div>
      </form>
    </div>
  );
};

export default AddAccount;